import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {Link, LinkNoCrawl} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import {clsx} from 'clsx';
import gql from 'graphql-tag';
import * as _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import {type SortFunction} from 'react-table';
import TimeAgo from 'react-timeago';
import {Popup} from 'semantic-ui-react';

import {
  type Maybe,
  type ReportsTableFragmentFragment,
  useStarViewMutation,
  useUnstarViewMutation,
  useUpsertView2Mutation,
} from '../generated/graphql';
import {type Graph} from '../types/graphql';
import {
  DISCUSSION_CATEGORY_ID,
  type ReportMetadata,
  useReportMetadata,
} from '../util/gallery';
import {isPublicRead} from '../util/projectAccess';
import * as Urls from '../util/urls';
import ImageIconHeader from './ImageIconHeader';
import {Skeleton} from './Skeleton';
import WBReactTable, {type WBReactTablePaginationMode} from './WBReactTable';

export interface ReportsTableReport {
  id: string;
  displayName: string;
  description: string;
  user: {
    id: string;
    username: string;
    photoUrl: string;
    admin: boolean;
  };
  project: {
    id: string;
    entityName: string;
    name: string;
    access: Maybe<string>;
  };
  createdAt?: string;
  updatedAt: string;
  starCount: number;
  starred: boolean;
  showcasedAt: Maybe<string>;
  previewUrl: Maybe<string>;
  viewCount?: number;
}

/**
 * Convert a `ReportsTableFragment` node from the generated GraphQL types
 * into the stricter `ReportsTableReport` shape that the table renders.
 *
 * Drops the optionality/nullability the schema exposes for fields the table
 * treats as required (`displayName`, `user`, `project`, `updatedAt`) by
 * returning `null` when any are missing — caller is expected to filter those
 * out — and defaults the rest (`description`, nested user fields, etc.) so
 * the renderer never sees `undefined`.
 *
 * Lets callers go from `data.entity.views` to `Graph<ReportsTableReport>`
 * without an `as unknown as` cast.
 */
export function reportFromViewNode(
  node: ReportsTableFragmentFragment | null | undefined
): ReportsTableReport | null {
  if (
    node == null ||
    !node.displayName ||
    !node.user ||
    !node.project ||
    !node.updatedAt
  ) {
    return null;
  }
  return {
    id: node.id,
    displayName: node.displayName,
    description: node.description ?? '',
    user: {
      id: node.user.id,
      username: node.user.username ?? '',
      photoUrl: node.user.photoUrl ?? '',
      admin: node.user.admin ?? false,
    },
    project: {
      id: node.project.id,
      entityName: node.project.entityName,
      name: node.project.name,
      access: node.project.access ?? null,
    },
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    starCount: node.starCount,
    starred: node.starred ?? false,
    showcasedAt: node.showcasedAt ?? null,
    previewUrl: node.previewUrl ?? null,
    viewCount: node.viewCount,
  };
}

/**
 * Map a connection's `edges` array (from any query that selects
 * `...ReportsTableFragment`) into a `Graph<ReportsTableReport>`. Skips
 * malformed nodes and the wrapping connection's `totalCount` / `__typename`.
 */
export function reportsGraphFromViewEdges(
  edges:
    | ReadonlyArray<{node?: ReportsTableFragmentFragment | null} | null>
    | undefined
): Graph<ReportsTableReport> {
  return {
    edges:
      edges
        ?.map(e => reportFromViewNode(e?.node))
        .filter((n): n is ReportsTableReport => n != null)
        .map(node => ({node})) ?? [],
  };
}

export type UseReportsListStateOptions = {
  defaultPageSize?: number;
};

export type ReportsListQueryVariables = {
  first: number;
  offset: number;
  displayNameContains: string | undefined;
};

export type UseReportsListStateResult = {
  searchText: string;
  setSearchText: (q: string) => void;
  page: number;
  setPage: (p: number) => void;
  pageSize: number;
  setPageSize: (s: number) => void;
  variables: ReportsListQueryVariables;
};

const REPORTS_LIST_DEFAULT_PAGE_SIZE = 10;

export function useReportsListState(
  options: UseReportsListStateOptions = {}
): UseReportsListStateResult {
  const {defaultPageSize = REPORTS_LIST_DEFAULT_PAGE_SIZE} = options;

  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const [prevSearchText, setPrevSearchText] = useState(searchText);
  const [prevPageSize, setPrevPageSize] = useState(pageSize);
  if (prevSearchText !== searchText || prevPageSize !== pageSize) {
    setPrevSearchText(searchText);
    setPrevPageSize(pageSize);
    setPage(0);
  }

  const variables = useMemo<ReportsListQueryVariables>(
    () => ({
      first: pageSize,
      offset: page * pageSize,
      displayNameContains: searchText || undefined,
    }),
    [pageSize, page, searchText]
  );

  return {
    searchText,
    setSearchText,
    page,
    setPage,
    pageSize,
    setPageSize,
    variables,
  };
}

// TODO: move into src/state/graphql
export const reportsTableFragment = gql`
  fragment ReportsTableFragment on View {
    id
    displayName
    description
    user {
      id
      username
      photoUrl
      admin
    }
    project {
      id
      name
      entityName
      access
    }
    starCount
    createdAt
    updatedAt
    starred
    showcasedAt
    previewUrl
    viewCount
  }
`;

type ReportsTableCommonProps = {
  data: Graph<ReportsTableReport> | null;
  showShowcase?: boolean;
  suppressHeader?: boolean;
  onSearch?: (query: string) => void;
  loading?: boolean;
  /**
   * Header rendered when `suppressHeader` is false. Defaults to the
   * "Reports" image-icon header; the EntityPage adapter passes a
   * `SectionTopHeader` instead.
   */
  header?: React.ReactNode;
  /**
   * Whether the person-column shows the report author (default) or the
   * owning entity. The EntityPage adapter picks `entity` off team pages.
   */
  authorColumnType?: 'author' | 'entity';
  /** Show a "Created On" column between the person column and "Last Edit". */
  showCreatedAtColumn?: boolean;
  /**
   * Predicate for whether a report is already showcased. Defaults to
   * checking `showcasedAt`; the EntityPage adapter checks the optimistic
   * `showcasedReportIDs` from context instead.
   */
  isReportShowcased?: (report: ReportsTableReport) => boolean;
  /**
   * Persist a showcase click. Defaults to the `upsertView` mutation; the
   * EntityPage adapter pushes into the context's `showcasedReportIDs`.
   * Analytics tracking happens in the component regardless.
   */
  onShowcase?: (report: ReportsTableReport) => void;
  /** Rendered when the table is empty and hidden. Defaults to `<div />`. */
  emptyComponent?: React.ReactNode;
  /** Keep the table mounted even when it has no rows (e.g. own profile). */
  keepVisibleWhenEmpty?: boolean;
  /** Forwarded to WBReactTable; shown when the table has no rows. */
  noDataComponent?: () => React.JSX.Element;
};

type ReportsTableClientPaginationProps = {
  paginationMode?: Extract<WBReactTablePaginationMode, 'client'>;
  pageSize?: number | null;
  onChangePageSize?: (pageSize: number) => void;
  page?: never;
  onPageChange?: never;
  totalCount?: never;
};

type ReportsTableServerPaginationProps = {
  paginationMode: Extract<WBReactTablePaginationMode, 'server'>;
  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  page: number;
  onPageChange: (page: number) => void;
  totalCount: number;
};

export type ReportsTableProps = ReportsTableCommonProps &
  (ReportsTableClientPaginationProps | ReportsTableServerPaginationProps);

const ReportsTable: React.FC<ReportsTableProps> = props => {
  const {
    data,
    showShowcase,
    onSearch,
    loading,
    header,
    authorColumnType = 'author',
    showCreatedAtColumn = false,
    isReportShowcased,
    onShowcase,
    emptyComponent = <div />,
    keepVisibleWhenEmpty = false,
    noDataComponent,
  } = props;
  const [starView] = useStarViewMutation();
  const [unstarView] = useUnstarViewMutation();
  const [upsertView] = useUpsertView2Mutation();

  const starReport = useCallback(
    (id: string) => starView({variables: {id}}),
    [starView]
  );
  const unstarReport = useCallback(
    (id: string) => unstarView({variables: {id}}),
    [unstarView]
  );
  const showcaseReport = useCallback(
    (r: ReportsTableReport, analyticsLocation: string) => {
      window.analytics?.track('Showcase Report Clicked', {
        location: analyticsLocation,
        reportName: r.displayName,
        reportID: r.id,
        projectName: r.project.name,
      });
      if (onShowcase != null) {
        onShowcase(r);
      } else {
        upsertView({
          variables: {id: r.id, showcasedAt: new Date().toISOString()},
        });
      }
    },
    [upsertView, onShowcase]
  );
  const reportIsShowcased = useCallback(
    (r: ReportsTableReport) =>
      isReportShowcased != null ? isReportShowcased(r) : r.showcasedAt != null,
    [isReportShowcased]
  );

  const toggleStarred = useCallback(
    (r: ReportsTableReport) =>
      r.starred ? unstarReport(r.id) : starReport(r.id),
    [unstarReport, starReport]
  );

  // The React Table cells are of type react.element so sorting is broken.
  // Extract the relevant values based on the element content. This is pretty
  // brittle so I need to figure out a more consistent way of doing this.
  // Edit this sort function if you add/remove columns from the Reports table.
  const defaultSortMethod: SortFunction = useCallback((a, b, desc) => {
    // check compare type, and set values accordingly
    let aVal = a;
    let bVal = b;
    // is date value
    if (a.props.date) {
      aVal = a.props.date;
      bVal = b.props.date;
    }
    // is showcase state
    else if (a.props.content) {
      aVal = a.props.content;
      bVal = b.props.content;
    }
    // is star count (children will be an array since it contains the heart
    // symbol and the heart count)
    else if (
      a.props.children &&
      Array.isArray(a.props.children) &&
      a.props.children.length === 2 &&
      a.props.children[0].props.name === 'heart'
    ) {
      aVal = a.props.children[1];
      bVal = b.props.children[1];
    }
    // is link
    else if (a.props.to) {
      aVal = (a.props.children as string).toLowerCase();
      bVal = (b.props.children as string).toLowerCase();
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  // If you edit this, edit defaultSortFunction() above to match.
  const columns = [
    {Header: 'Title', accessor: 'title', width: 300},
    ...(showShowcase
      ? [
          {
            Header: 'Showcase',
            accessor: 'showcase',
            width: 100,
            className: 'showcase-cell',
            headerClassName: 'showcase-cell',
          },
        ]
      : []),
    {Header: '', accessor: 'stars', width: 60, className: 'stars-cell'},
    {Header: 'Project', accessor: 'project', width: 200},
    authorColumnType === 'entity'
      ? {Header: 'Entity', accessor: 'entity'}
      : {Header: 'Author', accessor: 'author'},
    ...(showCreatedAtColumn
      ? [{Header: 'Created On', accessor: 'createdAt'}]
      : []),
    {Header: 'Last Edit', accessor: 'lastEdited'},
  ];

  // Reports without projects are either discussions or blog posts in FC,
  // grab their FC report metadata to form the correct link
  const projectlessReportIDs = useMemo(
    () =>
      (data?.edges ?? [])
        .filter(({node: r}) => r.project == null)
        .map(({node: r}) => r.id),
    [data]
  );
  const {loading: reportsLoading, reportMetadatas} =
    useReportMetadata(projectlessReportIDs);
  const projectlessReportByID = useMemo(() => {
    const reportMap = new Map<string, ReportMetadata>();
    if (!reportsLoading) {
      (reportMetadatas ?? []).forEach(r => {
        reportMap.set(r.id, r);
      });
    }
    return reportMap;
  }, [reportsLoading, reportMetadatas]);

  const reports = useMemo(
    () =>
      _.compact(
        data?.edges.map(({node: r}) => {
          // if we can't find a projectless report's FC metadata, omit it.
          const projectlessReportMetadata = projectlessReportByID.get(r.id);
          if (r.project == null && projectlessReportMetadata == null) {
            return null;
          }
          const showcaseDisabled =
            r.project != null ? !isPublicRead(r.project) : true;
          const link =
            r.project != null
              ? Urls.reportView({
                  entityName: r.project.entityName,
                  projectName: r.project.name,
                  reportID: r.id,
                  reportName: r.displayName,
                })
              : (projectlessReportMetadata?.tags?.some(
                  t => t.id === DISCUSSION_CATEGORY_ID
                )
                  ? Urls.galleryDiscussionView
                  : Urls.galleryPostEdit)({
                  entityName: projectlessReportMetadata?.entityName ?? '',
                  reportID: r.id,
                  reportName: r.displayName,
                });
          return {
            searchString: r.displayName,
            row: {
              title: <Link to={link}>{r.displayName}</Link>,
              ...(showShowcase
                ? {
                    showcase: !reportIsShowcased(r) ? (
                      <Popup
                        on="hover"
                        position="top center"
                        popperModifiers={{
                          preventOverflow: {enabled: false},
                        }}
                        content={
                          !showcaseDisabled
                            ? 'Showcase this report'
                            : 'Make this project public to showcase this report'
                        }
                        trigger={
                          <span>
                            <LegacyWBIcon
                              name="plus"
                              data-test="showcase-report-button"
                              className={clsx({
                                disabled: showcaseDisabled,
                              })}
                              onClick={() => {
                                if (showcaseDisabled) {
                                  return;
                                }

                                showcaseReport(r, 'reports table');
                              }}
                            />
                          </span>
                        }
                      />
                    ) : (
                      <Popup
                        on="hover"
                        position="top center"
                        popperModifiers={{
                          preventOverflow: {enabled: false},
                        }}
                        content="Featured at the top of your profile"
                        trigger={
                          <span>
                            <LegacyWBIcon
                              name="gallery"
                              data-test="go-to-showcase-button"
                              onClick={scrollToReportShowcase}
                            />
                          </span>
                        }
                      />
                    ),
                  }
                : {}),
              stars: (
                <div
                  style={{alignItems: 'center', display: 'flex', gap: '4px'}}>
                  <Icon
                    onClick={() => toggleStarred(r)}
                    name={r.starred ? 'star-filled' : 'star'}
                    style={{cursor: 'pointer'}}
                  />
                  {r.starCount}
                </div>
              ),
              project:
                r.project != null ? (
                  <LinkNoCrawl
                    to={`/${r.project.entityName}/${r.project.name}`}>
                    {r.project.name}
                  </LinkNoCrawl>
                ) : (
                  <></>
                ),
              entity:
                r.project?.entityName != null ? (
                  <LinkNoCrawl to={`/${r.project.entityName}`}>
                    {r.project.entityName}
                  </LinkNoCrawl>
                ) : (
                  <></>
                ),
              author: (
                <LinkNoCrawl to={`/${r.user.username}`}>
                  {r.user.username}
                </LinkNoCrawl>
              ),
              createdAt: r.createdAt
                ? new Date(r.createdAt).toLocaleDateString('sv-SE')
                : '',
              lastEdited: <TimeAgo date={r.updatedAt} />,
            },
          };
        })
      ),
    [
      data,
      projectlessReportByID,
      showShowcase,
      showcaseReport,
      toggleStarred,
      reportIsShowcased,
    ]
  );

  const renderHiddenRow = useCallback(
    (r: (typeof reports)[number]['row'], i: number) => (
      <div key={i} style={{display: 'none'}}>
        {r.title}
      </div>
    ),
    // eslint-disable-next-line
    []
  );

  // oxlint-disable-next-line react/use-memo
  const renderShimmerRow = useCallback(makeReportsTableShimmerRow, []);

  const keepsTableVisibleWhenEmpty =
    props.paginationMode === 'server' ||
    onSearch != null ||
    keepVisibleWhenEmpty;
  if (reports.length === 0 && !loading && !keepsTableVisibleWhenEmpty) {
    return <>{emptyComponent}</>;
  }

  return (
    <>
      {!props.suppressHeader &&
        (header ?? <ImageIconHeader icon="report" text="Reports" />)}
      <WBReactTable
        className="reports-table"
        data={reports}
        columns={columns}
        renderHiddenRow={renderHiddenRow}
        defaultSortMethod={defaultSortMethod}
        noDataComponent={noDataComponent}
        onSearch={onSearch}
        loading={loading}
        renderShimmerRow={renderShimmerRow}
        {...getReportsTablePaginationProps(props)}
      />
    </>
  );
};

export default ReportsTable;

function getReportsTablePaginationProps(props: ReportsTableProps) {
  if (props.paginationMode === 'server') {
    return {
      paginationMode: props.paginationMode,
      pageSize: props.pageSize,
      onChangePageSize: props.onChangePageSize,
      page: props.page,
      onPageChange: props.onPageChange,
      totalCount: props.totalCount,
    };
  }

  return {
    paginationMode: props.paginationMode,
    pageSize: props.pageSize ?? undefined,
    onChangePageSize: props.onChangePageSize,
  };
}

function scrollToReportShowcase() {
  const el = document.querySelector('#report-showcase');
  if (el != null) {
    el.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
  }
}

function makeReportsTableShimmerRow() {
  return {
    title: <Skeleton height={24} width="80%" />,
    showcase: <Skeleton height={24} width={24} />,
    stars: <Skeleton height={24} width={36} />,
    project: <Skeleton height={24} width="65%" />,
    entity: <Skeleton height={24} width="55%" />,
    author: <Skeleton height={24} width="55%" />,
    createdAt: <Skeleton height={24} width={88} />,
    lastEdited: <Skeleton height={24} width={80} />,
  };
}
